/**
 * CalendarV2 — 予約カレンダー v2
 */

import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import GridV2 from '../components/calendar2/GridV2';
import { Vehicle, Reservation, StatusItem } from '../components/calendar2/types';

// eslint-disable-next-line
import { useReservationData } from '../context/ReservationDataContext';
// eslint-disable-next-line
import { useReservationStatuses } from '../hooks/useReservationStatuses';
// eslint-disable-next-line
import { useAuth } from '../components/AuthContext';

interface CalendarV2Props {
  /** 車両ステータスID で事前フィルタするサブビュー（例: 米軍 = 6）。設定時は当該ステータスのみ表示し、フィルタモーダルから車両ステータスセクションを非表示にする */
  vehicleStatusFilterId?: number;
}

export default function CalendarV2({ vehicleStatusFilterId }: CalendarV2Props = {}) {
  const {
    reservations,
    vehicles: vehicleMap,
    vehicleTypes,
    isReady,
    isLoadingVehicles,
    isLoadingReservations,
  } = useReservationData() as {
    reservations: Reservation[];
    vehicles: Record<string, Vehicle>;
    vehicleTypes: any[];
    isReady: boolean;
    isLoadingVehicles: boolean;
    isLoadingReservations: boolean;
  };

  const {
    vehicleStatusList,
    reservationStatusList,
    vehicleLocationStatusList,
  } = useReservationStatuses() as {
    vehicleStatusList: StatusItem[];
    reservationStatusList: StatusItem[];
    vehicleLocationStatusList: StatusItem[];
  };

  const { user } = (useAuth?.() ?? {}) as { user?: any };

  const vehicleList = Object.values(vehicleMap) as Vehicle[];

  // サブビューの事前フィルタ: vehicleStatusFilterId が指定されていれば当該ステータスのみ
  const filteredVehicleList = useMemo(() => {
    if (vehicleStatusFilterId == null) return vehicleList;
    return vehicleList.filter(v => Number(v.status) === vehicleStatusFilterId);
  }, [vehicleList, vehicleStatusFilterId]);

  // ヘッダ用ラベル（vehicleStatusList から該当 id の label を引く）
  const headerSubtitle = useMemo(() => {
    if (vehicleStatusFilterId == null) return undefined;
    return vehicleStatusList.find(s => Number(s.id) === vehicleStatusFilterId)?.label;
  }, [vehicleStatusFilterId, vehicleStatusList]);

  if (!isReady) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" gap={2}>
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          {isLoadingVehicles ? '車両データを読み込み中...' : isLoadingReservations ? '予約データを読み込み中...' : '準備中...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      <GridV2
        vehicles={filteredVehicleList}
        reservations={reservations}
        vehicleTypes={vehicleTypes}
        vehicleStatusList={vehicleStatusList}
        reservationStatusList={reservationStatusList}
        locationStatusList={vehicleLocationStatusList}
        user={user}
        hideStatusFilter={vehicleStatusFilterId != null}
        hideTypeFilter={vehicleStatusFilterId != null}
        headerSubtitle={headerSubtitle}
        filterCookieKey={vehicleStatusFilterId != null ? `calv2_vehicle_filter_status_${vehicleStatusFilterId}` : undefined}
      />
    </Box>
  );
}
